<template>
  <div class="sticky top-0 z-50 border-b border-gray-200 bg-white">
    <div class="px-48 py-2">
      <button
        class="flex items-center gap-2 text-gray-500 underline transition-colors hover:text-gray-700"
        @click="goBack"
      >
        <ArrowLeft class="h-4 w-4" />
        <span class="text-sm">
          {{ returnsToDocumentsView ? t('backToDocuments') : t('back') }}
        </span>
      </button>
    </div>

    <div class="py-2 pl-48 pr-4">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('pdfEditor') }}</h1>
        <div class="flex items-center gap-3">
          <span class="text-sm text-gray-600">
            {{ organizedDocuments.length }} {{ t('files') }}
          </span>

          <!-- Drag Mode Indicator -->
          <div
            v-if="isDragging && dragMode"
            class="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200"
            :class="dragModeStyles.container"
          >
            <div
              class="h-2 w-2 rounded-full transition-colors duration-200"
              :class="dragModeStyles.indicator"
            ></div>
            <span>{{ dragModeText }}</span>
          </div>

          <Button
            variant="secondary"
            class="flex items-center gap-2"
            :disabled="organizedDocuments.length === 0"
            @click="downloadAsPdf"
          >
            <Download class="h-4 w-4" />
            {{ t('downloadAsPdf') }}
          </Button>
          <Button
            :disabled="!hasChanges || isSaving || organizedDocuments.length === 0"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:cursor-not-allowed
              disabled:opacity-50"
            @click="saveDocuments"
          >
            <Loader
              v-if="isSaving"
              class="h-4 w-4 animate-spin"
            />
            <span v-else>{{ t('saveDocuments') }}</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { Button } from '@/components/ui/button'
  import { ArrowLeft, Download, Loader } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import { useDocumentOrganizationContext } from './composables/useDocumentOrganization'
  import { useToast } from '@/components/ui/toast'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { DocumentGroup } from './composables/useDocumentOrganization'
  import type { Document } from '@/types/generated'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  interface Props {
    organizedDocuments: DocumentGroup[]
    documents: Document[]
    originalDocumentState: DocumentGroup[]
    hasChanges: boolean
    caseId: string
    pageRotations: Map<string, number> | Record<string, number>
    returnPath?: string
  }

  const props = defineProps<Props>()

  const router = useRouter()
  const { toast } = useToast()
  const store = useCaseStore()
  const { trackEvent } = useMetrics()

  const { t: tComposable } = useTranslation({
    en: {
      backToDocuments: 'Back to all documents',
      back: 'Back',
      pdfEditor: 'PDF editor',
      files: 'files',
      downloadAsPdf: 'Download as PDF',
      saveDocuments: 'Save documents',
      error: 'Error',
      downloadError: 'Failed to download PDFs'
    },
    de: {
      backToDocuments: 'Zurück zu allen Dokumenten',
      back: 'Zurück',
      pdfEditor: 'PDF-Editor',
      files: 'Dateien',
      downloadAsPdf: 'Als PDF herunterladen',
      saveDocuments: 'Dokumente speichern',
      error: 'Fehler',
      downloadError: 'Download der PDFs fehlgeschlagen'
    }
  })

  const {
    getDragModeText,
    getDragModeStyles,
    downloadOrganizedPdfs,
    downloadOriginalPdfs,
    saveDocumentReorganization,
    isDragging,
    dragMode,
    isSaving,
    setSavingState,
    resetState,
    pendingNameChanges
  } = useDocumentOrganizationContext()

  const dragModeText = getDragModeText
  const dragModeStyles = getDragModeStyles

  // Use the translation function from the composable
  const t = tComposable

  const returnsToDocumentsView = computed(() => {
    if (!props.returnPath) return true
    return props.returnPath.split('?')[0] === `/app/cases/${props.caseId}/documents`
  })

  const goBack = () => {
    if (props.returnPath) {
      router.push(props.returnPath)
    } else {
      router.push(`/app/cases/${props.caseId}/documents`)
    }
  }

  /**
   * Analyze changes between original and current state
   */
  const analyzeChanges = () => {
    const originalGroups = props.originalDocumentState
    const currentGroups = props.organizedDocuments

    const analysis = {
      documents_added: Math.max(0, currentGroups.length - originalGroups.length),
      documents_removed: Math.max(0, originalGroups.length - currentGroups.length),
      documents_merged:
        originalGroups.length > currentGroups.length
          ? originalGroups.length - currentGroups.length
          : 0,
      documents_split:
        currentGroups.length > originalGroups.length
          ? currentGroups.length - originalGroups.length
          : 0,
      total_pages_original: originalGroups.reduce((sum, g) => sum + g.pages.length, 0),
      total_pages_current: currentGroups.reduce((sum, g) => sum + g.pages.length, 0),
      pages_moved: 0,
      pages_deleted: 0,
      pages_rotated: Object.keys(props.pageRotations).length,
      document_groups_renamed: 0
    }

    // Calculate pages moved/deleted by comparing UUIDs
    const originalPageUuids = new Set(originalGroups.flatMap((g) => g.pages.map((p) => p.uuid)))
    const currentPageUuids = new Set(currentGroups.flatMap((g) => g.pages.map((p) => p.uuid)))

    analysis.pages_deleted = originalPageUuids.size - currentPageUuids.size

    // Check for renamed documents
    originalGroups.forEach((orig, idx) => {
      if (currentGroups[idx] && orig.name !== currentGroups[idx].name) {
        analysis.document_groups_renamed++
      }
    })

    return analysis
  }

  /**
   * Download documents as PDF based on current state
   */
  const downloadAsPdf = async () => {
    const analysis = props.hasChanges ? analyzeChanges() : null

    // Track download started
    trackEvent(MetricEventType.PDF_ORGANIZE_DOWNLOAD_STARTED, {
      case_uuid: props.caseId,
      document_count: props.organizedDocuments.length,
      has_changes: props.hasChanges,
      ...(analysis ?? {})
    })

    try {
      if (props.hasChanges) {
        // Download organized PDFs (one per document group)
        await downloadOrganizedPdfs(props.organizedDocuments, props.pageRotations)
      } else {
        // Download original PDFs as they were uploaded
        await downloadOriginalPdfs(props.documents)
      }

      // Track download completed
      trackEvent(MetricEventType.PDF_ORGANIZE_DOWNLOAD_COMPLETED, {
        case_uuid: props.caseId,
        document_count: props.organizedDocuments.length,
        has_changes: props.hasChanges
      })
    } catch (error) {
      console.error('Error downloading PDFs:', error)

      // Track download failed
      trackEvent(MetricEventType.PDF_ORGANIZE_DOWNLOAD_FAILED, {
        case_uuid: props.caseId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      toast({
        title: t('error'),
        description: t('downloadError'),
        variant: 'destructive'
      })
    }
  }

  /**
   * Save document reorganization changes
   */
  const saveDocuments = async () => {
    if (isSaving.value) return // Prevent multiple simultaneous saves

    setSavingState(true)

    // Analyze changes before saving
    const analysis = analyzeChanges()

    // Determine operation types
    const operations: string[] = []
    if (analysis.documents_merged > 0) operations.push('merge')
    if (analysis.documents_split > 0) operations.push('split')
    if (analysis.pages_moved > 0) operations.push('reorder')
    if (analysis.pages_deleted > 0) operations.push('delete')
    if (analysis.pages_rotated > 0) operations.push('rotate')
    if (analysis.document_groups_renamed > 0) operations.push('rename')
    if (operations.length === 0) operations.push('none')

    // Track save started
    trackEvent(MetricEventType.PDF_ORGANIZE_CHANGES_SAVED, {
      case_uuid: props.caseId,
      document_count_before: props.originalDocumentState.length,
      document_count_after: props.organizedDocuments.length,
      operation_types: operations,
      ...analysis
    })

    try {
      const success = await saveDocumentReorganization(
        props.caseId,
        props.organizedDocuments,
        props.originalDocumentState,
        props.documents,
        props.hasChanges,
        props.pageRotations,
        pendingNameChanges.value,
        // Success callback
        async () => {
          // Reload case data to ensure fresh document list
          await store.loadCase(props.caseId)

          await resetState()
          if (props.returnPath) {
            router.push(props.returnPath)
          } else {
            router.push(`/app/cases/${props.caseId}/documents`)
          }
        },
        // Error callback
        (error: any) => {
          console.error('Error in save callback:', error)
        }
      )

      if (!success) {
        setSavingState(false)
      }
    } catch (error) {
      console.error('Error saving documents:', error)
      setSavingState(false)
    }
  }
</script>
